import { useReducer } from 'react'
import { useOnEvent } from '@sketch/utils'
import { useGetComponentsCountQuery } from '@sketch/gql-types'
import { useGetIdentifiers } from './useGetIdentifiers'

interface CountTotalComponentsProps {
  loading: boolean
  symbolsCount: number
  layerStylesCount: number
  textStylesCount: number
  colorVarsCount: number
}

const countTotalComponents = (componentsCount: CountTotalComponentsProps) => {
  const {
    loading,
    symbolsCount,
    layerStylesCount,
    textStylesCount,
    colorVarsCount,
  } = componentsCount

  if (
    loading ||
    symbolsCount === undefined ||
    layerStylesCount === undefined ||
    textStylesCount === undefined ||
    colorVarsCount === undefined
  ) {
    return false
  }

  return symbolsCount + textStylesCount + layerStylesCount + colorVarsCount
}

export const useGetComponentsCount = () => {
  const { shareIdentifier, versionShortId } = useGetIdentifiers()

  const { loading, data, error } = useGetComponentsCountQuery({
    variables: {
      shareIdentifier,
      versionShortId,
    },
    skip: !shareIdentifier || !versionShortId,
  })

  // This is just to force re-render
  const [, forceUpdate] = useReducer(x => x + 1, 0)

  // This event is triggered when a new version is processed, when this happens
  // we need to re-render this component again to calculate the components number of each type
  useOnEvent('versionIsProcessed', share => {
    if (share.identifier === shareIdentifier) {
      forceUpdate()
    }
  })

  const hasComponentManifest = data?.share?.version?.hasComponentManifest
  const componentsState = data?.share?.version?.document?.componentsState
  const symbolsCount = data?.share?.version?.document?.componentCount.symbol!
  const layerStylesCount = data?.share?.version?.document?.componentCount
    .layerStyle!
  const textStylesCount = data?.share?.version?.document?.componentCount
    .textStyle!
  const colorVarsCount = data?.share?.version?.document?.componentCount
    .colorVar!

  const hideCounters =
    !hasComponentManifest &&
    ['CAN_PROCESS', 'CANNOT_PROCESS'].includes(
      componentsState ?? 'CANNOT_PROCESS'
    )

  const totalComponents = countTotalComponents({
    loading,
    symbolsCount,
    layerStylesCount,
    textStylesCount,
    colorVarsCount,
  })

  return {
    loading,
    error,
    totalComponents,
    symbolsCount: hideCounters ? undefined : symbolsCount,
    symbolsLabel: `Symbols ${hideCounters ? '' : `(${symbolsCount})`}`,
    layerStylesCount: hideCounters ? undefined : layerStylesCount,
    layerStylesLabel: `Layer Styles ${
      hideCounters ? '' : `(${layerStylesCount})`
    }`,
    textStylesCount: hideCounters ? undefined : textStylesCount,
    textStylesLabel: `Text Styles ${
      hideCounters ? '' : `(${textStylesCount})`
    }`,
    colorVarsCount: hideCounters ? undefined : colorVarsCount,
    colorVarsLabel: `Color Variables ${
      hideCounters ? '' : `(${colorVarsCount})`
    }`,
  }
}
